import { NewTag } from "@/components/Tag";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import React, { useState } from "react";
import { useMediaQuery } from "usehooks-ts";

interface SectionListItemProps {
  title: React.ReactNode;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export const SectionListItem: React.FC<SectionListItemProps> = ({
  icon,
  title,
  children,
  disabled,
  onClick,
}) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <div
      className={cn(
        "flex flex-row justify-between items-center h-13 mobile:h-10 px-3",
        disabled
          ? "text-dark-disabled pointer-events-none cursor-default"
          : "text-dark-tertiary group-hover:text-white",
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {icon && (
          <div
            className={cn(
              "h-7 w-7 flex items-center justify-center shrink-0",
              isMobile && "w-8 h-8",
            )}
          >
            {icon}
          </div>
        )}
        <div
          className={cn(
            "text-sm leading-none truncate min-w-0",
            isMobile && "text-base",
          )}
        >
          {title}
        </div>
      </div>
      {children}
    </div>
  );
};
SectionListItem.displayName = "SectionListItem";

interface SectionListSectionProps {
  text?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  searchbar?: React.ReactNode;
  searchResults?: React.ReactNode;
}

export const SectionListSection: React.FC<SectionListSectionProps> = ({
  text,
  icon,
  children,
  searchbar,
  searchResults,
}) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <span
          className={cn("text-dark-tertiary text-sm", isMobile && "text-base")}
        >
          {text}
        </span>
        {icon}
      </div>
      {searchbar && <div className="">{searchbar}</div>}
      {searchResults && (
        <div className="mb-3 rounded-lg bg-[#333A42]">{searchResults}</div>
      )}
      <div className="rounded-lg bg-[#333A42]">{children}</div>
    </div>
  );
};

interface SectionListProps {
  header?: React.ReactNode;
  accordion?: boolean;
  locked?: boolean;
  children: React.ReactNode;
  closedClassName?: string;
  isOpen?: boolean;
  rangeFilter?: React.ReactNode;
  isNew?: boolean;
}

interface SectionListComponent extends React.FC<SectionListProps> {
  Item: React.FC<SectionListItemProps>;
  Section: React.FC<SectionListSectionProps>;
}

const SectionList: SectionListComponent = ({
  header,
  accordion = false,
  locked,
  children,
  closedClassName,
  isOpen: isOpenProp,
  rangeFilter,
  isNew,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? true);

  const toggleAccordion = () => {
    if (accordion) setIsOpen((prev) => !prev);
  };

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <div>
      <header
        className={cn(
          "bg-panel-foreground flex items-center px-4 h-11 text-dark-secondary",
          isMobile && "h-12.5",
          accordion ? "justify-between" : "justify-start",
          accordion && "cursor-pointer",
          !isOpen && closedClassName,
        )}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleAccordion();
        }}
      >
        <div
          className={cn(
            "text-sm flex flex-row items-center gap-x-2",
            isMobile && "text-base",
            locked && "text-dark-secondary",
          )}
        >
          {header}
          {isNew && <NewTag />}
        </div>
        {rangeFilter && (
          <div
            className="ml-auto mr-4"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {rangeFilter}
          </div>
        )}
        {accordion && (
          <div
            className={cn(
              "w-3 h-3 text-dark-tertiary",
              isOpen ? "rotate-180" : "",
            )}
          >
            {arrow}
          </div>
        )}
      </header>
      {(!accordion || isOpen) && (
        <div className="relative overflow-hidden">
          <main className="h-auto">{children}</main>
        </div>
      )}
    </div>
  );
};

SectionList.Item = SectionListItem;
SectionList.Section = SectionListSection;

export default SectionList;
